import { supabase } from '../../../lib/supabase'
import type { ColorfulRoomRow } from '../../../lib/database.types'

/** One row per queued game-type per user. */
export interface OnlineQueueStatus {
  queued_count: number
  i_am_queued: boolean
}

export interface EnterQueueResult {
  matched: boolean
  room_id: string | null
  queued_count: number
}

/** Puts the caller in queue for a game type. Auto-matches at 4. */
export async function enterOnlineQueue(
  gameType: 'ideatthat' | 'colorful' | 'tokyo_metro' | 'three_step',
): Promise<EnterQueueResult> {
  const { data, error } = await supabase.rpc('enter_online_queue', {
    p_game_type: gameType,
  })
  if (error) throw error
  // Postgres returns a table-valued function as an array of one row.
  const row = Array.isArray(data) ? data[0] : data
  return {
    matched: Boolean(row?.matched),
    room_id: row?.room_id ?? null,
    queued_count: Number(row?.queued_count ?? 0),
  }
}

export async function leaveOnlineQueue(
  gameType: 'ideatthat' | 'colorful' | 'tokyo_metro' | 'three_step',
): Promise<void> {
  const { error } = await supabase.rpc('leave_online_queue', {
    p_game_type: gameType,
  })
  if (error) throw error
}

export async function fetchOnlineQueueStatus(
  gameType: 'ideatthat' | 'colorful' | 'tokyo_metro' | 'three_step',
): Promise<OnlineQueueStatus> {
  const { data, error } = await supabase.rpc('online_queue_status', {
    p_game_type: gameType,
  })
  if (error) throw error
  const row = Array.isArray(data) ? data[0] : data
  return {
    queued_count: Number(row?.queued_count ?? 0),
    i_am_queued: Boolean(row?.i_am_queued),
  }
}

/**
 * Returns the caller's current matchmade Colorful room if one exists
 * (lobby/playing), otherwise null. Waiting clients poll this to pick
 * up a match that was fired by another player joining the queue.
 */
export async function fetchMyMatchmadeColorfulRoom(): Promise<ColorfulRoomRow | null> {
  const { data, error } = await supabase.rpc('my_matchmade_colorful_room', {})
  if (error) throw error
  const row = data as ColorfulRoomRow | null
  if (!row || !row.id) return null
  return row
}

/** Count of matchmade games the caller is currently a player in. */
export async function fetchMyActiveOnlineGameCount(): Promise<number> {
  const { data, error } = await supabase.rpc('my_active_online_game_count', {})
  if (error) throw error
  return Number(data ?? 0)
}
